import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useEffect, useRef } from "react";
import moment from "moment/moment";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

var timeout, current_chart;
var scroll_event_happening = false;

export default function IngressEgressChart({
  labels,
  ingress,
  egress,
  title,
  chartType = "inout", // "inout" | "net"
  onHoverEpoch,
  onZoom,
  startLimit,
  endLimit
}) {

  const wheelRef = useRef(null);
  const chartRef = useRef(null);

  const egressNeg = (egress ?? []).map((v) => -Number(v ?? 0));
  const net = (ingress ?? []).map((v, i) => Number(v ?? 0) - Number(egress?.[i] ?? 0));

  const runWheelScrollEvent = ({ chart }) => {
    var start_time, end_time, group_size;
    let min_limit = moment(0)
    let max_limit = moment();
    // console.log(chart.scales.x.min)
    let min_moment = moment(chart.scales.x.min);
    let max_moment = moment(chart.scales.x.max);

    if (min_moment.isBefore(min_limit)) {
      start_time = min_limit;
    }
    else {
      start_time = min_moment;
    }

    if (max_moment.isAfter(max_limit)) {
      end_time = max_limit;
    }
    else {
      end_time = max_moment;
    }
    setTimeout(function () {
      // console.log("set start time")
      onZoom({ newStart: start_time.unix(), newEnd: end_time.unix() })
    })


  };


  const scrollEvent = (chart) => {
    // console.log(timeout);
    current_chart = chart;
    if (typeof timeout != 'undefined') {
      clearTimeout(timeout);
      timeout = undefined;
      // console.log('timeout cleared')
    }

    timeout = setTimeout(() => {
      // console.log('running scroll event - timeout expired')
      // console.log(scroll_event_happening);

      runWheelScrollEvent(current_chart);

      clearTimeout(timeout)
      timeout = undefined;
      scroll_event_happening = false;
    }, 400);
  }

  const datasets =
    chartType === "net"
      ? [
        {
          label: "Net (in - out)",
          data: net,
          tension: 0.25,
          pointRadius: 0
        }
      ]
      : [
        {
          label: "Ingress (in)",
          data: ingress ?? [],
          tension: 0.25,
          pointRadius: 0
        },
        {
          label: "Egress (out)",
          data: egressNeg,
          tension: 0.25,
          pointRadius: 0
        }
      ];

  const data = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (evt, elements, chart) => {
      if (!chart) return;

      const xScale = chart.scales.x;
      if (!xScale) return;

      const epoch = xScale.getValueForPixel(evt.x);
      if (epoch != null) {
        onHoverEpoch?.(epoch / 1000);
      }
    },
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: true },
      zoom: {
        zoom: {
          mode: 'x',
          drag: {
            enabled: false,
          },
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: false,
          },
          speed: 0.5, sensitivity: 0.5,
          onZoomComplete: function (chart) {
            // console.log("on zoom complete");
            // console.log(scroll_event_happening);
            scrollEvent(chart)
          },
          onZoom: function () {
            // console.log("on zoom")
            // console.log(scroll_event_happening)
            scroll_event_happening = true;
          }
        },
        limits: {
          x: {
            min: startLimit * 1000,
            max: endLimit * 1000,
            minRange: 1000,
          }
        },
        title: {
          display: false,
          text: ""
        },
        tooltip: {
          enabled: true,
        }
      },
      tooltip: {
        callbacks: {
          // Show egress as positive numbers in tooltip even though plotted negative
          label: (ctx) => {
            const name = ctx.dataset.label || "";
            const y = Number(ctx.parsed.y ?? 0);
            if (name.toLowerCase().includes("egress")) return `${name}: ${Math.abs(y)}`;
            return `${name}: ${y}`;
          }
        }
      }
    },
    scales: {
      x: { type: 'linear' },
      y: { beginAtZero: true } // allow negative scale
    }
  };


  return (
    <Card>
      <SoftBox p={3} height={800}>
        <SoftTypography variant="h6">{title}</SoftTypography>

        <SoftBox
          mt={2}
          sx={{
            height: 280,
            overscrollBehavior: "contain",
            touchAction: "none"
          }}
        >
          <Line ref={chartRef} data={data} options={options} />
        </SoftBox>
      </SoftBox>
    </Card>
  );
}
